import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Eye, EyeOff, Lock, AlertCircle } from 'lucide-react';
import api from '../../api';
import useModalEffect from '../../hooks/useModalEffect';
import Alert from '../Alert/Alert';

const ChangePasswordModal = ({ isOpen, onClose, onSubmit }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingPassword, setIsValidatingPassword] = useState(false);
  const [validationTimeout, setValidationTimeout] = useState(null);
  const [errorAlert, setErrorAlert] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setFormErrors({});
      setShowPasswords({
        current: false,
        new: false,
        confirm: false
      });
      setErrorAlert(null);
    }
  }, [isOpen]);

  // Handle modal show/hide using Bootstrap
  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
      };
      
      cleanupModal();
      
      // Clear validation timeout
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Function to validate current password
  const validateCurrentPassword = async () => {
    console.log('=== VALIDATE CURRENT PASSWORD CALLED ===');
    console.log('Current password:', formData.currentPassword);
    
    if (!formData.currentPassword.trim()) {
      console.log('Password is empty, skipping validation');
      return;
    }

    // Clear existing timeout
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    // Immediate validation without debouncing for onBlur
    setIsValidatingPassword(true);
    
    try {
      // Get current user info from localStorage or context
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('User info from localStorage:', userInfo);
      
      const username = userInfo.username;
      console.log('Username for validation:', username);
      
      if (!username) {
        console.log('No username found');
        setFormErrors(prev => ({
          ...prev,
          currentPassword: 'User information not found'
        }));
        return;
      }

      console.log('Calling API /api/auth/cekPassword...');
      const response = await api.post('/auth/cekPassword', {
        username: username,
        password: formData.currentPassword
      });

      console.log('API Response:', response);
      console.log('Password validation successful');

      // Password is correct
      setFormErrors(prev => ({
        ...prev,
        currentPassword: ''
      }));
      
    } catch (error) {
      console.log('Password validation failed:', error);
      console.log('Error response:', error.response?.data);
      
      // Password is incorrect
      setFormErrors(prev => ({
        ...prev,
        currentPassword: 'Current password is incorrect'
      }));
    } finally {
      setIsValidatingPassword(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.currentPassword.trim()) {
      errors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword.trim()) {
      errors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = 'New password must be at least 8 characters long';
    } else if (!/[A-Z]/.test(formData.newPassword)) {
      errors.newPassword = 'New password must include at least one uppercase letter';
    } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword)) {
      errors.newPassword = 'New password must include at least one special character';
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('=== CHANGE PASSWORD SUBMIT ===');
      console.log('Form data:', formData);
      
      // Get current user info
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      const username = userInfo.username;
      
      if (!username) {
        throw new Error('User information not found');
      }

      // Call change password API
      const response = await api.post('/auth/change-password', {
        username: username,
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      console.log('Change password response:', response);

      if (response.data.success) {
        console.log('Password changed successfully');
        if (onSubmit) onSubmit(formData);
      } else {
        throw new Error(response.data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to change password';
      setErrorAlert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    
    
    if (onClose) onClose();
  };

  return (
    <div
      className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
      id="modal-change-password"
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Change Password</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {errorAlert && (
                <Alert
                  type="danger"
                  message={errorAlert}
                  onClose={() => setErrorAlert(null)}
                />
              )}

              {/* Password Requirements Alert */}
              <div className="alert alert-warning alert-dismissible" role="alert">
                <div className="alert-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                    <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                    <path d="M12 8v4"></path>
                    <path d="M12 16h.01"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="alert-heading">Ensure that these requirements are met:</h4>
                  <div className="alert-description">
                    <ul className="alert-list">
                      <li>Minimum 8 characters long</li>
                      <li>Include uppercase letter</li>
                      <li>Include special character</li>
                    </ul>
                  </div>
                </div>
                <a className="btn-close" data-bs-dismiss="alert" aria-label="close"></a>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label required">Current Password</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <Lock size={16} />
                      </span>
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        className={`form-control ${formErrors.currentPassword ? 'is-invalid' : formData.currentPassword && !isValidatingPassword && !formErrors.currentPassword ? 'is-valid' : ''}`}
                        placeholder="Enter your current password"
                        value={formData.currentPassword}
                        onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                        onBlur={validateCurrentPassword}
                        disabled={isSubmitting || isValidatingPassword}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility('current')}
                        disabled={isSubmitting || isValidatingPassword}
                      >
                        {isValidatingPassword ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />
                        )}
                      </button>
                      {formErrors.currentPassword && (
                        <div className="invalid-feedback d-flex align-items-center">
                          <AlertCircle size={16} className="me-1" />
                          {formErrors.currentPassword}
                        </div>
                      )}
                      {formData.currentPassword && !isValidatingPassword && !formErrors.currentPassword && (
                        <div className="valid-feedback d-flex align-items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                            <polyline points="20,6 9,17 4,12"></polyline>
                          </svg>
                          Password is correct
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label required">New Password</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <Lock size={16} />
                      </span>
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        className={`form-control ${formErrors.newPassword ? 'is-invalid' : ''}`}
                        placeholder="Enter your new password"
                        value={formData.newPassword}
                        onChange={(e) => handleInputChange('newPassword', e.target.value)}
                        disabled={isSubmitting}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility('new')}
                        disabled={isSubmitting}
                      >
                        {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      {formErrors.newPassword && (
                        <div className="invalid-feedback d-flex align-items-center">
                          <AlertCircle size={16} className="me-1" />
                          {formErrors.newPassword}
                        </div>
                      )}
                    </div>
                    <div className="form-text">
                      Password must be at least 8 characters long, include uppercase letter and special character
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label required">Confirm New Password</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <Lock size={16} />
                      </span>
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        className={`form-control ${formErrors.confirmPassword ? 'is-invalid' : ''}`}
                        placeholder="Confirm your new password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        disabled={isSubmitting}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility('confirm')}
                        disabled={isSubmitting}
                      >
                        {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      {formErrors.confirmPassword && (
                        <div className="invalid-feedback d-flex align-items-center">
                          <AlertCircle size={16} className="me-1" />
                          {formErrors.confirmPassword}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-link link-secondary" 
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary ms-auto" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
